import { getResizedImage } from 'shared-helpers/common';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

import type { CommonBlockPropsReader } from 'components/reader/blocks';

interface Props extends Pick<CommonBlockPropsReader<'discussion'>, 'fontSize'> {
  attachment: string;
  attachmentType: 'image' | 'file';
  disabled: boolean;
  onRemove: () => void;
}

const ReaderBlockDiscussionCommentAttachment = ({
  attachment,
  attachmentType,
  disabled,
  fontSize,
  onRemove
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div
      className="attachment"
      style={{
        fontSize: {
          small: '1.3rem',
          medium: '1.4rem',
          large: '1.5rem',
          huge: '1.6rem'
        }[fontSize]
      }}
    >
      {attachmentType === 'image' ? (
        <div className="image">
          <img
            alt={attachment.slice(attachment.lastIndexOf('/') + 1)}
            className="attachment-image"
            sizes="100vw"
            src={getResizedImage({ url: attachment, size: 640 })}
            srcSet={
              `${getResizedImage({ url: attachment, size: 1920 })} 1920w,` +
              `${getResizedImage({ url: attachment, size: 1280 })} 1280w,` +
              `${getResizedImage({ url: attachment, size: 960 })} 960w,` +
              `${getResizedImage({ url: attachment, size: 640 })} 640w,` +
              `${getResizedImage({ url: attachment, size: 480 })} 480w`
            }
          />

          <button
            aria-label={messages.readerDiscussionRemoveImage}
            className="image-remove"
            disabled={disabled}
            data-tooltip={messages.readerDiscussionRemoveImage}
            onClick={onRemove}
          >
            <Icon icon="times" />
          </button>
        </div>
      ) : (
        <div className="link">
          {attachment.slice(attachment.lastIndexOf('/') + 1)}

          <button
            aria-label={messages.readerDiscussionRemoveFile}
            className="link-remove"
            disabled={disabled}
            data-tooltip={messages.readerDiscussionRemoveFile}
            onClick={onRemove}
          >
            <Icon icon="times" />
          </button>
        </div>
      )}

      <style jsx>{`
        .image {
          display: inline-block;
          margin-right: 10px;
          max-width: 440px;
          position: relative;
        }
        .attachment-image {
          max-width: 100%;
        }
        .image-remove {
          border: 0;
          background-color: rgba(200, 200, 200, 0.7);
          border-radius: 5px;
          color: ${theme.textColor};
          cursor: pointer;
          outline: 0;
          padding: 6px 9px;
          position: absolute;
          right: 10px;
          top: 10px;
          transition: background-color 0.3s;
        }
        .iamge-remove:focus,
        .image-remove:hover {
          background-color: rgba(200, 200, 200, 0.9);
        }
        .image-remove:disabled {
          background-color: rgba(200, 200, 200, 0.7);
          cursor: default;
        }
        .link {
          background-color: ${theme.lightGrey};
          border-radius: 5px;
          color: inherit;
          display: inline-flex;
          font-weight: 600;
          max-width: 100%;
          padding: 4px 40px 4px 10px;
          position: relative;
          word-break: break-word;
        }
        .link-remove {
          border: 0;
          background-color: transparent;
          border-radius: 3px;
          color: ${theme.textColorLighter};
          cursor: pointer;
          outline: 0;
          padding: 0 7px;
          position: absolute;
          bottom: 3px;
          right: 3px;
          top: 3px;
        }
        .link-remove:focus,
        .link-remove:hover {
          background-color: ${theme.lightGreyDark};
        }
        .link-remove:disabled {
          background-color: transparent;
          cursor: default;
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockDiscussionCommentAttachment;
